import { FC, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { QUAY_BOLLARDS } from '@/components/blocks/berth-plan/berth-plan.utils';
import { FormTextInput } from '@/components/form-fields/form-text-input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { dateTimeLocalToISOString } from '@/lib/utils';
import {
  CreateShipVisitSchema,
  getCreateShipVisitSchema,
} from '@/routes/(app)/(scenarios)/(create)/types';

interface CreateShipVisitDialogProps {
  open: boolean;
  cutoffMs?: number;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateShipVisitSchema) => void;
}

const CreateShipVisitDialog: FC<CreateShipVisitDialogProps> = ({
  open,
  cutoffMs,
  onOpenChange,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const schema = useMemo(() => getCreateShipVisitSchema(cutoffMs), [cutoffMs]);
  const form = useForm<CreateShipVisitSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: '',
      arrival_time: '',
      departure_time: '',
      ship_id: '',
      shipping_line_id: '',
      bollard_fore: undefined,
      bollard_aft: undefined,
      n_moves_load: 0,
      n_moves_discharge: 0,
    },
  });

  const handleSubmit = (data: CreateShipVisitSchema) => {
    onSubmit({
      ...data,
      arrival_time: dateTimeLocalToISOString(data.arrival_time),
      departure_time: dateTimeLocalToISOString(data.departure_time),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}>
      <DialogContent className='z-100'>
        <DialogHeader>
          <DialogTitle>{t('shipVisits.dialog.title')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className='grid grid-cols-2 gap-4'>
            <FormTextInput
              control={form.control}
              name='id'
              label={t('shipVisits.table.id')}
              className='col-span-2'
            />
            <FormTextInput
              control={form.control}
              name='ship_id'
              label={t('shipVisits.table.ship_id')}
              className='col-span-2'
            />
            <FormTextInput
              control={form.control}
              name='shipping_line_id'
              label={t('shipVisits.table.shipping_line_id')}
              className='col-span-2'
            />
            <FormTextInput
              type='datetime-local'
              control={form.control}
              name='arrival_time'
              label={t('shipVisits.table.arrival_time')}
              className='col-span-1'
            />
            <FormTextInput
              type='datetime-local'
              control={form.control}
              name='departure_time'
              label={t('shipVisits.table.departure_time')}
              className='col-span-1'
            />
            <FormTextInput
              type='number'
              control={form.control}
              name='bollard_fore'
              label={t('shipVisits.table.bollard_fore')}
              className='col-span-1'
              min={1}
              max={QUAY_BOLLARDS}
            />
            <FormTextInput
              type='number'
              control={form.control}
              name='bollard_aft'
              label={t('shipVisits.table.bollard_aft')}
              className='col-span-1'
              min={1}
              max={QUAY_BOLLARDS}
            />
            <FormTextInput
              type='number'
              control={form.control}
              name='n_moves_load'
              label={t('shipVisits.form.containersToLoad')}
              className='col-span-2'
            />
            <FormTextInput
              type='number'
              control={form.control}
              name='n_moves_discharge'
              label={t('shipVisits.form.containersToDischarge')}
              className='col-span-2'
            />
            <DialogFooter className='col-span-2'>
              <Button type='submit'>{t('shipVisits.dialog.create')}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateShipVisitDialog;
